import { useWindowProperties } from '@qred/components-library';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import AppShell from '~/components/shared/AppShell/AppShell';
import Navbar from '~/components/shared/Navbar/Navbar';
import Stepper from '~/components/shared/Stepper/Stepper';
import { TopFixedContainer } from '~/components/shared/TopFixedContainer/TopFixedContainer';
import { getHomePageByMarket } from '~/constants/markets';
import { StepStatus } from '~/enums';
import { areSameCompanies } from '~/helpers/utils';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import {
  patchCardApplication,
  postCardApplication,
} from '~/store/actions/cardApplicationActions';
import {
  goToPreviousStep,
  setCurrentStepName,
} from '~/store/slices/cardApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import { StepsContainer } from '~/styles/BaseStyledComponents';
import { cardApplicationSteps } from './cardApplicationSteps';

export const Steps = () => {
  const dispatch = useAppDispatch();

  const { isMobile } = useWindowProperties();
  const {
    apiStatus,
    currentStepNumber,
    form,
    currentApplicationOrganizationIdentifier,
  } = useSelector((state: RootState) => state.cardApplication);
  const { market, language } = useSelector((state: RootState) => state.intl);

  const currentStep = cardApplicationSteps[currentStepNumber - 1];
  const isFirstStep = currentStepNumber === 1;
  const isLastStep = currentStepNumber === cardApplicationSteps.length;
  const stepIsLoading =
    apiStatus.post === ApiStatus.Started ||
    apiStatus.patch === ApiStatus.Started;

  const onBackButtonClick = () => {
    dispatch(
      trackCardApplicationActions({
        actionName: 'back_button_pressed',
      })
    );

    if (isFirstStep) {
      window.location.assign(getHomePageByMarket(market, language));
    } else {
      dispatch(goToPreviousStep());
      dispatch(trackCardApplicationActions({ stepStatus: StepStatus.Edit }));
    }
  };

  const onSubmit = () => {
    dispatch(
      trackCardApplicationActions({
        actionName: 'submit_button_pressed',
      })
    );
    dispatch(patchCardApplication({ finalized: true }));
  };

  const onNextStepClick = () => {
    dispatch(
      trackCardApplicationActions({
        actionName: 'next_button_pressed',
      })
    );
    const companyHasChanged = !areSameCompanies(
      form.organizationNumber,
      currentApplicationOrganizationIdentifier
    );

    if (companyHasChanged) {
      dispatch(postCardApplication({ shouldGoToNextStep: true }));
    } else {
      dispatch(patchCardApplication({ shouldGoToNextStep: true }));
    }
  };

  useEffect(() => {
    dispatch(setCurrentStepName(currentStep.name));
    dispatch(
      trackCardApplicationActions({
        stepStatus: StepStatus.Started,
      })
    );
  }, [currentStep.name]);

  return (
    <AppShell>
      <TopFixedContainer
        style={{ maxWidth: '800px', width: '100%' }}
        topFixedDisabled={!isMobile}
      >
        <Navbar
          onBackClick={onBackButtonClick}
          showBackButton
          progressStepperDots={{
            steps: cardApplicationSteps.map((step) => ({
              name: step.name,
              hideInProgressStepper: step.hideInProgressStepper,
            })),
            currentStep: currentStepNumber,
          }}
        />
      </TopFixedContainer>

      <StepsContainer>
        <Stepper
          currentStepConfig={currentStep}
          currentStep={currentStepNumber}
          footerCTAAction={isLastStep ? onSubmit : onNextStepClick}
          footerCTALabel={isLastStep ? 'Onboarding.Submit' : 'Onboarding.Next'}
          stepIsLoading={stepIsLoading}
        />
      </StepsContainer>
    </AppShell>
  );
};
